// src/pages/suppliers/EditSupplierPage.tsx

import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FiSlash, FiX } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import UpdateSupplierForm from "@/components/suppliers/forms/UpdateSupplierForm";

import {
  type SupplierFormData,
  type SupplierRow,
  mapSupplierDeepToForm,
  mapFormToUpdateBody,
} from "./useSuppliersPage";

import { useSupplier } from "@/api/features/suppliers/queries";
import {
  useDisableSupplierUserMutation,
  useUpdateSupplierMutation,
} from "@/api/features/suppliers/mutations";

/* ---------------------- Breadcrumbs (same pattern) ----------------------- */
function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const label = p
      .split("-")
      .map((s) => s[0]?.toUpperCase() + s.slice(1))
      .join(" ");
    return { href, label };
  });

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            <span>/</span>
            {i < crumbs.length - 1 ? (
              <Link to={c.href} className="hover:text-gray-700">
                {c.label}
              </Link>
            ) : (
              <span className="text-gray-700">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ============================================================================
   Page: Edit Supplier
============================================================================ */
export default function EditSupplierPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Optional: name passed from list page for nicer title
  // navigate(`/suppliers/edit/${row.id}`, { state: { supplier: row } })
  const state = location.state as { supplier?: SupplierRow } | null;
  const supplierFromState = state?.supplier;

  const {
    data: supplier,
    isLoading: supplierLoading,
    isError: supplierError,
  } = useSupplier(id ?? "");

  const updateMutation = useUpdateSupplierMutation();
  const disableUserMutation = useDisableSupplierUserMutation();

  const handleCancel = () => {
    navigate("/suppliers");
  };

  const handleSubmit = (form: SupplierFormData) => {
    if (!id) return;

    // Deep update: base + products + contacts all go in a single payload
    const body = mapFormToUpdateBody(form);

    updateMutation.mutate(
      { id, data: body },
      {
        onSuccess: () => {
          navigate("/suppliers");
        },
      }
    );
  };

  const handleSendPassword = (
    // email: string
  ) => {
    // TODO: wire this to backend endpoint that sends login details
    // For now, keep as a no-op / log:
    // eslint-disable-next-line no-console
    // console.log("Send supplier password email to:", email);
  };

  const handleDisableUser = () => {
    if (!id) return;
    const yes = window.confirm(
      "Disable login for this approved supplier? They will no longer be able to sign in."
    );
    if (!yes) return;
    disableUserMutation.mutate(id);
  };

  // Derive form error message from mutation error
  let errorMessage: string | null = null;
  const err: any = updateMutation.error;
  if (err) {
    if (typeof err === "string") {
      errorMessage = err;
    } else if (err?.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err?.message) {
      errorMessage = err.message;
    } else {
      errorMessage = "Failed to update supplier. Please try again.";
    }
  }

  // --- Render states ---

  if (!id) {
    return (
      <AdminLayout>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Edit Supplier
            </h1>
            <div className="mt-1">
              <Breadcrumbs />
            </div>
          </div>

          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FiX />
            Back to Suppliers
          </button>
        </div>

        <section className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          <p>Missing supplier id in URL.</p>
        </section>
      </AdminLayout>
    );
  }

  if (supplierLoading) {
    return (
      <AdminLayout>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Edit Supplier
            </h1>
            <div className="mt-1">
              <Breadcrumbs />
            </div>
          </div>

          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FiX />
            Back to Suppliers
          </button>
        </div>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-600">
          Loading supplier details…
        </section>
      </AdminLayout>
    );
  }

  if (supplierError || !supplier) {
    return (
      <AdminLayout>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Edit Supplier
            </h1>
            <div className="mt-1">
              <Breadcrumbs />
            </div>
          </div>

          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FiX />
            Back to Suppliers
          </button>
        </div>

        <section className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          <p>
            Supplier could not be loaded for ID{" "}
            <span className="font-mono">{id}</span>. Please go back to the
            suppliers list and try again.
          </p>
        </section>
      </AdminLayout>
    );
  }

  // Build initial form value from deep supplier (already includes products + contacts)
  const initialForm: SupplierFormData = mapSupplierDeepToForm(supplier);

  const titleName =
    supplierFromState?.companyName ||
    supplier.companyName ||
    supplier.supplierCompanyName;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Edit Supplier{titleName ? ` — ${titleName}` : ""}
          </h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {supplier.status === "APPROVED" && (
            <button
              type="button"
              onClick={handleDisableUser}
              disabled={disableUserMutation.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-60"
            >
              <FiSlash />
              {disableUserMutation.isPending ? "Disabling..." : "Disable User"}
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FiX />
            Back to Suppliers
          </button>
        </div>
      </div>

      {/* Form card */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <UpdateSupplierForm
          value={initialForm}
          onSubmit={handleSubmit}
          onSendPassword={handleSendPassword}
          onCancel={handleCancel}
          submitting={updateMutation.isPending}
          errorMessage={errorMessage}
        />
      </section>
    </AdminLayout>
  );
}
